/**
 * 「点消息区 → 焦点回输入框」的判据（21x，人工要求「点击消息默认聚焦输入框」）。
 *
 * 纯函数单独放，是因为**该不该聚焦**的边界全是坑，塞在 JSX 里会越加越乱：
 *
 * - **只在桌面**：移动端聚焦会弹出软键盘，点一下消息就弹键盘是灾难。
 * - **多选态不聚焦**：那时点行是勾选，输入框本来就藏着。
 * - **选中了文字不聚焦**：用户拖选气泡文字准备复制，松手是一次 click；
 *   这时 focus() 会把文档选区收掉，复制就没了。判据是选区非折叠。
 * - **点在交互元素上不聚焦**：链接/按钮（图片、语音、文件、@、重试…）各有各的
 *   行为，抢焦点会干扰它们（例如打开图片查看器后 Esc 该给查看器而不是输入框）。
 */
export interface FocusContext {
  readonly isMobile: boolean;
  readonly selecting: boolean;
  /** 当前文档选区是否为空（默认读 window.getSelection；测试可注入） */
  readonly selectionCollapsed?: boolean;
}

const INTERACTIVE_TAGS = new Set(['A', 'BUTTON', 'INPUT', 'TEXTAREA', 'SELECT']);

/** 点击落点是否在「有自己行为」的元素里（含祖先） */
function hitsInteractive(target: EventTarget | null): boolean {
  // 按 tagName/parentElement **鸭子类型**走，不用 `instanceof Element`：
  // 后者在 node 环境（单测）下连 Element 都没有，而这段逻辑本身不需要 DOM。
  // event.target 非元素时（document/window）没有 tagName，循环直接结束。
  let el = target as { tagName?: unknown; parentElement?: unknown } | null;
  while (el !== null && el !== undefined && typeof el.tagName === 'string') {
    if (INTERACTIVE_TAGS.has(el.tagName)) return true;
    el = el.parentElement as { tagName?: unknown; parentElement?: unknown } | null;
  }
  return false;
}

function documentSelectionCollapsed(): boolean {
  const sel = typeof window === 'undefined' ? null : window.getSelection();
  return sel === null || sel.isCollapsed;
}

export function focusComposerOnBodyClick(
  e: { target: EventTarget | null },
  ctx: FocusContext,
): boolean {
  if (ctx.isMobile || ctx.selecting) return false;
  const collapsed = ctx.selectionCollapsed ?? documentSelectionCollapsed();
  if (!collapsed) return false;
  return !hitsInteractive(e.target);
}
